/**
 * Explore earn amounts — credited to profile mCoins (shop-spendable).
 * Idempotent per drop id via explorePointAwards for find (pickup) and new-place create.
 */

import { doc, increment, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { distanceMeters } from './fogReveal';
import { EARN_COINS, awardMapHas, getEarnCoins } from '../users/earnCatalog';
import { getUserProfile, USERS_COLLECTION } from '../users/userProfile';

export const EXPLORE_POINT_REWARDS = Object.freeze({
  PICKUP: EARN_COINS.DROP_PICKUP,
  /** Awarded only when create is ≥ CREATE_MIN_SPACING_M from all prior own drops. */
  CREATE: EARN_COINS.DROP_CREATE,
});

/** Min distance (m) from any of the author's earlier drops to earn create coins. */
export const CREATE_MIN_SPACING_M = 5;

/** @deprecated Prefer reading mCoins; kept as alias for call sites. */
export function explorePointsFromProfile(profile) {
  const n = Number(profile?.mCoins);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

export function formatExplorePointsLabel(points) {
  const n = Math.max(0, Math.floor(Number(points) || 0));
  return n < 10 ? `0${n}` : String(n);
}

function awardsMap(profile, kind) {
  const root = profile?.explorePointAwards;
  if (!root || typeof root !== 'object') return {};
  const map = root[kind];
  return map && typeof map === 'object' ? map : {};
}

export function hasExplorePickupAward(profile, dropId) {
  if (!dropId) return false;
  return awardMapHas(awardsMap(profile, 'pickups'), dropId);
}

export function hasExploreCreateAward(profile, dropId) {
  if (!dropId) return false;
  return awardMapHas(awardsMap(profile, 'creates'), dropId);
}

/**
 * True when (lng,lat) is a "new place" vs the author's prior drops (≥ CREATE_MIN_SPACING_M).
 * First drop (empty prior list) always qualifies.
 */
export function isExploreCreateNewPlace(lng, lat, priorOwnDrops = [], minSpacingM = CREATE_MIN_SPACING_M) {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  const spacing = Number.isFinite(minSpacingM) && minSpacingM > 0 ? minSpacingM : CREATE_MIN_SPACING_M;
  const priors = Array.isArray(priorOwnDrops) ? priorOwnDrops : [];
  for (let i = 0; i < priors.length; i += 1) {
    const p = priors[i];
    if (!p || !Number.isFinite(p.lng) || !Number.isFinite(p.lat)) continue;
    if (distanceMeters(lng, lat, p.lng, p.lat) < spacing) return false;
  }
  return true;
}

/**
 * Chronological walk of one author's drops → set of drop ids that earn create coins.
 * `drops` items: `{ id, lng, lat, createdAt? }` (createdAt optional; sort uses id tie-break).
 */
export function qualifyingCreateDropIds(drops, minSpacingM = CREATE_MIN_SPACING_M) {
  const list = (Array.isArray(drops) ? drops : [])
    .filter((d) => d?.id && Number.isFinite(d.lng) && Number.isFinite(d.lat))
    .slice()
    .sort((a, b) => {
      const ta = createdAtMillis(a.createdAt);
      const tb = createdAtMillis(b.createdAt);
      if (ta !== tb) return ta - tb;
      return String(a.id).localeCompare(String(b.id));
    });

  const awarded = [];
  const prior = [];
  list.forEach((drop) => {
    if (isExploreCreateNewPlace(drop.lng, drop.lat, prior, minSpacingM)) {
      awarded.push(drop.id);
      prior.push({ lng: drop.lng, lat: drop.lat });
    } else {
      prior.push({ lng: drop.lng, lat: drop.lat });
    }
  });
  return awarded;
}

function createdAtMillis(value) {
  if (value == null) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Award find coins once per drop id. Returns coins earned (0 if already awarded).
 */
export async function awardExplorePickupPoints(uid, dropId, profile = null) {
  if (!uid || !dropId) return 0;
  const existing = profile || (await getUserProfile(uid));
  if (hasExplorePickupAward(existing, dropId)) return 0;
  const earned = getEarnCoins().DROP_PICKUP;
  const patch = {
    [`explorePointAwards.pickups.${dropId}`]: earned,
    updatedAt: serverTimestamp(),
  };
  if (earned > 0) patch.mCoins = increment(earned);
  await updateDoc(doc(db, USERS_COLLECTION, uid), patch);
  return earned;
}

/**
 * Award create coins once per drop id when placement is a new place vs prior own drops.
 * Returns coins earned (0 if already awarded, too close, or missing coords).
 *
 * @param {object} [placement]
 * @param {number} placement.lng
 * @param {number} placement.lat
 * @param {Array<{lng:number,lat:number}>} [placement.priorOwnDrops]
 */
export async function awardExploreCreatePoints(uid, dropId, profile = null, placement = null) {
  if (!uid || !dropId) return 0;
  const existing = profile || (await getUserProfile(uid));
  if (hasExploreCreateAward(existing, dropId)) return 0;

  const lng = Number(placement?.lng);
  const lat = Number(placement?.lat);
  const priorOwnDrops = placement?.priorOwnDrops;
  if (!isExploreCreateNewPlace(lng, lat, priorOwnDrops)) return 0;

  const earned = getEarnCoins().DROP_CREATE;
  const patch = {
    [`explorePointAwards.creates.${dropId}`]: earned,
    updatedAt: serverTimestamp(),
  };
  if (earned > 0) patch.mCoins = increment(earned);
  await updateDoc(doc(db, USERS_COLLECTION, uid), patch);
  return earned;
}
